import React from 'react';
import {
  useGetOne,
  useRedirect,
  useResourceContext,
  useDelete,
  useNotify,
  useRefresh
} from 'react-admin';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';

interface ResourceBreadcrumbsWithActionsProps {
  // Optional: Override the resource name
  resource?: string;
  // Optional: Custom display names
  customNames?: {
    singular?: string;
    plural?: string;
  };
  // Optional: Parent resource info for nested resources
  parentResource?: {
    name: string;
    id: string | number;
    displayName?: string;
  };
  // Optional: Hide certain actions
  hideEdit?: boolean;
  hideDelete?: boolean;
  // Optional: Custom delete message
  deleteMessage?: string;
  // Optional: Override listUrl
  listUrl?: string;
  // Optional: Custom edit handler
  onEdit?: () => void;
  // Optional: Custom delete handler
  onDelete?: () => void;
}

const ResourceBreadcrumbsWithActions: React.FC<ResourceBreadcrumbsWithActionsProps> = ({
  resource: resourceProp,
  customNames,
  parentResource,
  hideEdit = false,
  hideDelete = false,
  deleteMessage,
  listUrl,
  onEdit,
  onDelete,
}) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const resourceContext = useResourceContext();
  const { id } = useParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  // Use prop resource or context resource
  const resource = resourceProp || resourceContext;

  // Fetch the current item data
  const { data: itemData, isLoading } = useGetOne(
    resource,
    { id },
    { enabled: !!id && !!resource }
  );

  // Setup delete mutation
  const [deleteOne, { isLoading: isDeleting }] = useDelete(
    resource,
    { id: id as string }
  );

  if (!resource || !id) {
    return null;
  }

  // Resource display name mappings
  const resourceMap: Record<string, { singular: string; plural: string }> = {
    equipment: { singular: 'Vehicle', plural: 'Vehicles' },
    users: { singular: 'User', plural: 'Users' },
    drivers: { singular: 'Driver', plural: 'Drivers' },
    customers: { singular: 'Customer', plural: 'Customers' },
    leads: { singular: 'Lead', plural: 'Leads' },
    opportunities: { singular: 'Opportunity', plural: 'Opportunities' },
    tasks: { singular: 'Task', plural: 'Tasks' },
    'equipment-maintenance': { singular: 'Maintenance', plural: 'Maintenance Records' },
    'equipment-fuel': { singular: 'Fuel Record', plural: 'Fuel Records' },
    'equipment-incidents': { singular: 'Incident', plural: 'Incidents' },
    'equipment-inspections': { singular: 'Inspection', plural: 'Inspections' },
    'equipment-assignments': { singular: 'Assignment', plural: 'Assignments' },
    brands: { singular: 'Brand', plural: 'Brands' },
    regions: { singular: 'Region', plural: 'Regions' },
    enterprises: { singular: 'Enterprise', plural: 'Enterprises' },
  };

  // Get resource names
  const resourceNames = customNames ||
    resourceMap[resource] || {
    singular: resource.charAt(0).toUpperCase() + resource.slice(1),
    plural: resource.charAt(0).toUpperCase() + resource.slice(1) + 's',
  };

  // Handle navigation
  const handleNavigation = (path: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    redirect(path);
  };

  // Handle browser back navigation
  const handleBackNavigation = () => {
    window.history.back();
  };

  // Handle edit action
  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      redirect(`/${resource}/${id}/edit`);
    }
  };

  // Handle delete action
  const handleDelete = async () => {
    try {
      await deleteOne();
      notify(`${resourceNames.singular} deleted successfully`, { type: 'success' });
      redirect(listUrl || `/${resource}`);
    } catch (error) {
      notify(`Error deleting ${resourceNames.singular.toLowerCase()}`, { type: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return null;
  }

  // Determine the item display name
  const getItemDisplayName = () => {
    if (!itemData) {
      return `${resourceNames.singular} ${id}`;
    }

    // For customers/leads, use displayName first
    if ((resource === 'customers' || resource === 'leads')) {
      if (itemData.displayName) {
        return itemData.displayName;
      }
      // Fallback to constructed name
      if (itemData.firstName || itemData.lastName) {
        return `${itemData.firstName || ''} ${itemData.lastName || ''}`.trim() || itemData.company || `${resourceNames.singular} ${id}`;
      }
    }

    // For drivers, try to get user name
    if (resource === 'drivers' && itemData.user?.name) {
      return itemData.user.name;
    }

    // For users, try email
    if (resource === 'users' && itemData.email) {
      return itemData.email;
    }

    // Common name fields to check for other resources
    const nameFields = ['name', 'title', 'label', 'displayName'];
    for (const field of nameFields) {
      if (itemData[field]) {
        return itemData[field];
      }
    }

    return `${resourceNames.singular} ${id}`;
  };

  const itemDisplayName = getItemDisplayName();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '16px 20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: '#f1f5f9',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {/* Back Navigation Button */}
        <IconButton
          onClick={handleBackNavigation}
          sx={{
            width: 36,
            height: 36,
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            '&:hover': {
              backgroundColor: '#1976d2',
              color: 'white',
              borderColor: '#1976d2',
            },
            transition: 'all 0.2s ease',
          }}
          title="Go back"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        {/* Enhanced Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: '#64748b' }} />}
          aria-label="breadcrumb"
          sx={{ flex: 1 }}
        >
          {/* Parent resource link if provided */}
          {parentResource && (
            <Link
              underline="hover"
              color="inherit"
              href={`/${parentResource.name}/${parentResource.id}/show`}
              onClick={handleNavigation(`/${parentResource.name}/${parentResource.id}/show`)}
              sx={{
                fontWeight: 'medium',
                fontSize: '0.95rem',
                color: '#475569',
                '&:hover': {
                  color: '#1976d2',
                  textDecoration: 'underline',
                },
                transition: 'color 0.2s ease',
              }}
            >
              {parentResource.displayName || parentResource.name}
            </Link>
          )}

          {/* Link to the list view */}
          <Link
            underline="hover"
            color="inherit"
            href={listUrl || `/${resource}`}
            onClick={handleNavigation(listUrl || `/${resource}`)}
            sx={{
              fontWeight: 'medium',
              fontSize: '0.95rem',
              color: '#475569',
              '&:hover': {
                color: '#1976d2',
                textDecoration: 'underline',
              },
              transition: 'color 0.2s ease',
            }}
          >
            {resourceNames.plural}
          </Link>

          {/* Current item name */}
          <Typography
            color="text.primary"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#1e293b',
            }}
          >
            {itemDisplayName}
          </Typography>
        </Breadcrumbs>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!hideEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{
                borderColor: '#e2e8f0',
                color: '#475569',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#1976d2',
                  color: 'white',
                },
              }}
            >
              Edit
            </Button>
          )}

          {!hideDelete && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => {
                if (onDelete) {
                  onDelete();
                } else {
                  setDeleteDialogOpen(true);
                }
              }}
              sx={{
                borderColor: '#e2e8f0',
                color: '#ef4444',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: '#ef4444',
                  color: 'white',
                },
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete {resourceNames.singular}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {deleteMessage || `Are you sure you want to delete "${itemDisplayName}"? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResourceBreadcrumbsWithActions;